import React from 'react';

interface CardProps {
  children: React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  highlight?: 'correct' | 'incorrect' | 'none';
}

const Card: React.FC<CardProps> = ({
  children,
  onClick,
  selected = false,
  disabled = false,
  className = '',
  highlight = 'none',
}) => {
  // Base robotic classes
  const baseClasses = `
    relative overflow-hidden rounded-xl p-6 transition-all duration-300 group
    bg-gradient-to-br from-dark-800 to-dark-900
    border border-dark-700 shadow-lg backdrop-blur-sm
    before:absolute before:inset-0 before:bg-grid-pattern before:bg-grid before:opacity-20
  `;
  
  // Interactive classes
  const interactiveClasses = onClick && !disabled
    ? `cursor-pointer hover:shadow-neon hover:border-primary-500 hover:scale-[1.02]
       hover:bg-gradient-to-br hover:from-dark-700 hover:to-dark-800
       active:scale-[0.98] active:shadow-inner-glow`
    : '';
  
  // Selected classes
  const selectedClasses = selected
    ? `ring-2 ring-primary-500 shadow-neon border-primary-500
       bg-gradient-to-br from-primary-950 to-dark-900`
    : '';
  
  // Disabled classes
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed hover:scale-100 hover:shadow-none'
    : '';
  
  // Highlight classes with robotic styling
  const highlightClasses = {
    none: '',
    correct: `
      border-accent-500 bg-gradient-to-br from-accent-950 to-dark-900
      shadow-[0_0_20px_rgba(34,197,94,0.3)] animate-pulse-slow
    `,
    incorrect: `
      border-red-500 bg-gradient-to-br from-red-950 to-dark-900
      shadow-[0_0_20px_rgba(239,68,68,0.3)] animate-pulse-slow
    `,
  };
  
  // Combine all classes
  const cardClasses = `
    ${baseClasses}
    ${interactiveClasses}
    ${selectedClasses}
    ${disabledClasses}
    ${highlightClasses[highlight]}
    ${className}
  `.replace(/\s+/g, ' ').trim();
  
  return (
    <div
      className={cardClasses}
      onClick={!disabled && onClick ? onClick : undefined}
    >
      {/* Corner brackets */}
      <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-primary-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
      <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-primary-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
      <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-primary-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
      <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-primary-500 opacity-60 group-hover:opacity-100 transition-opacity"></div>
      
      {/* Scanning line effect */}
      {onClick && !disabled && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent animate-scan"></div>
        </div>
      )}
      
      {/* Content with relative positioning */}
      <div className="relative z-10">
        {children}
      </div>
      
      {/* Highlight overlay for correct/incorrect states */}
      {highlight !== 'none' && (
        <div className={`absolute inset-0 pointer-events-none ${
          highlight === 'correct'
            ? 'bg-gradient-to-r from-accent-500/10 to-accent-400/10'
            : 'bg-gradient-to-r from-red-500/10 to-red-400/10'
        }`}></div>
      )}
    </div>
  );
};

export default Card;